import { useDebounce, useFetch } from "@4uhub/lib4uhub";
import { Box, Grid } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GridModeHeader } from "../../../../../components/Dashboards/GridModeHeader/GridModeHeader";
import { ViewMore } from "../../../../../components/Dashboards/ViewMore/ViewMore";
import NoData from "../../../../../components/Logs/NoData/NoData";
import { IDashBoardPanel } from "../../../../../models/dashboardpanel";
import MainApiService from "../../../../../services/mainApi.service";
import { useDashboardView } from "../../../../../store/contexts/DashBoardViewContext";
import { DashboardCard } from "./DashboardCard/DashboardCard";

const DASHBOARD_VIEW_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/DashboardPanel/GetViewWithPagination/";

const dashboardViewService = new MainApiService<IDashBoardPanel>(
  DASHBOARD_VIEW_ROUTE
);

export const DashboardGrid = memo(() => {
  const { t } = useTranslation();

  const { dashboards, setDashboards } = useDashboardView();

  const [hasNextPage, setHasNextPage] = useState(false);

  const [page, setPage] = useState(1);

  const [text, setText] = useState("");

  const searchBy = useDebounce(text, 500);

  const { sendRequest, loading } = useFetch(dashboardViewService.getAll);

  const fetchDashboards = useCallback(async () => {
    const { data, success } = await sendRequest({
      PageNumber: page,
      PageSize: 25,
      SearchBy: searchBy,
    });
    if (data && success) {
      setHasNextPage(data.hasNextPage);
      if (searchBy.length > 0) {
        setDashboards(data.items);
        return;
      }
      setDashboards((prev) => [...prev, ...data.items]);
    }
  }, [sendRequest, setDashboards, page, searchBy]);

  const viewMoreHandler = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const searchHandler = useCallback(
    (value: string) => {
      setDashboards([]);
      setText(value);
    },
    [setDashboards]
  );

  useEffect(() => {
    fetchDashboards();
  }, [fetchDashboards]);

  return (
    <>
      <GridModeHeader searchHandler={searchHandler} />
      <Box
        sx={{
          mt: 4,
          pb: 2,
          height: "100%",
          overflow: "auto",
        }}
      >
        {!loading && dashboards.length === 0 && (
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <NoData message={t("page.panels.no_data")} />
          </Box>
        )}
        {!loading && dashboards.length > 0 && (
          <Grid container spacing={2}>
            {dashboards.map((dashboard) => (
              <Grid item xs={12} md={3} xl={2} key={dashboard.id}>
                <DashboardCard dashboard={dashboard} />
              </Grid>
            ))}
          </Grid>
        )}
        {(hasNextPage || loading) && (
          <ViewMore
            loading={loading}
            hasNextPage={hasNextPage}
            viewMoreHandler={viewMoreHandler}
          />
        )}
      </Box>
    </>
  );
});
